import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { CobaltToggle } from '@cae/cobalt-react/components/toggle';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import * as React from 'react';

import type { NotificationsPreferencesPanelProps } from './NotificationsPreferencesPanel.types';

import { useCreateUserNotificationPreference } from '../../../hooks/use-create-user-notification-preference/useCreateUserNotificationPreference';
import { useUpdateUserNotificationPreference } from '../../../hooks/use-update-user-notification-preference/useUpdateUserNotificationPreference';
import { REASON_CODE } from '../Notifications.constants';
import styles from './NotificationsPreferencesPanel.module.css';

const NotificationsPreferencesPanel = React.memo(
  ({
    isOpen,
    onClose,
    notificationPreferences,
  }: Readonly<NotificationsPreferencesPanelProps>): React.ReactElement => {
    const isTabletAndDesktop = useMediaQuery(MediaQuery.TabletAndDesktop);
    const panelSize = isTabletAndDesktop ? 'small' : 'large';

    const { mutate: createMutate, reset: createReset, isPending: isCreatePending, isError: isCreateError } = useCreateUserNotificationPreference();
    const { mutate: updateMutate, reset: updateReset, isPending: isUpdatePending, isError: isUpdateError } = useUpdateUserNotificationPreference();

    const hasSmsFromApi =
      notificationPreferences?.definition?.preferredChannels?.includes('sms') ??
      false;
    const [hasSms, setHasSms] = React.useState(hasSmsFromApi);
    React.useEffect((): void => {
      setHasSms(hasSmsFromApi);
    }, [hasSmsFromApi]);
    const onToggleSms = React.useCallback((): void => {
      setHasSms(prev => !prev);
    }, []);
    const hasToggleChanged = hasSms !== hasSmsFromApi;

    const saveSelection = React.useCallback((): void => {
      const currentChannels =
        notificationPreferences?.definition?.preferredChannels ?? [];
      const updatedChannels = hasSms
        ? [...new Set([...currentChannels, 'sms' as const])]
        : currentChannels.filter(ch => ch !== 'sms');

      const mutationOptions = {
        onSuccess: (): void => {
          emitFlagMessage('success', 'Changes saved successfully!', 5000);
          onClose();
        },
      };

      if (notificationPreferences?.id) {
        updateMutate(
          {
            id: notificationPreferences.id,
            reasonCode: notificationPreferences.definition?.reasonCode ?? REASON_CODE,
            preferredChannels: updatedChannels,
          },
          mutationOptions
        );
      } else {
        createMutate(
          {
            reasonCode: REASON_CODE,
            preferredChannels: updatedChannels,
          },
          mutationOptions
        );
      }
    }, [
      notificationPreferences,
      hasSms,
      onClose,
      updateMutate,
      createMutate,
    ]);

    const isSaving = isCreatePending || isUpdatePending;
    const isMutationError = isCreateError || isUpdateError;

    const handleCancel = React.useCallback((): void => {
      setHasSms(hasSmsFromApi);
      createReset();
      updateReset();
      onClose();
    }, [hasSmsFromApi, onClose, createReset, updateReset]);

    return (
      <CobaltSidePanel
        isOpen={isOpen}
        showCloseButton={true}
        size={panelSize}
        surtitle="Notifications"
        title="Train: Schedule changes"
        description="Receive a notification when your schedule gets updated."
        onClose={handleCancel}
        data-heap-id="notifications-side-panel"
        actionBar={
          <CobaltActionBar
            mobile={!isTabletAndDesktop}
            position="right"
            primaryButton={
              <CobaltButton
                data-heap-id="profile-notifications-save-btn"
                disabled={!hasToggleChanged || isSaving}
                onClick={saveSelection}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </CobaltButton>
            }
            secondaryButton={
              <CobaltButton
                data-heap-id="profile-notifications-cancel-btn"
                variant="secondary"
                onClick={handleCancel}
              >
                Cancel
              </CobaltButton>
            }
          />
        }
      >
        {isSaving && <ContentLoader />}
        {isMutationError && (
          <div className={styles.errorMessage}>
            <CobaltSectionMessage
              variant="danger"
              sectionMessageTitle={
                <h3>We couldn't save your information. Please try again.</h3>
              }
              data-heap-id="mutation-error-banner"
            />
          </div>
        )}
        <div className={styles.panelWrapper}>
          <div className={styles.panelTextContent}>
            <span className={`body-02`}>SMS</span>
            <p className={`body-01 ${styles.secondaryText}`}>
              Message and data rates might apply. Messages are sent to the phone
              number confirmed during check‑in.
            </p>
          </div>
          <CobaltToggle
            id="profile-notifications-sms-toggle"
            data-heap-id="profile-notifications-sms-toggle"
            checked={hasSms}
            onChange={onToggleSms}
          />
        </div>
      </CobaltSidePanel>
    );
  }
);

NotificationsPreferencesPanel.displayName = 'NotificationsPreferencesPanel';
export { NotificationsPreferencesPanel };
